import { gatewayApi as api } from './gatewayApi';

// Interfaces
export interface Status {
  Id?: number;
  StatusName: string;
  Description: string;
  Active: boolean;
  CreatedDate?: string;
  ModifiedDate?: string;
}

export interface StatusListResponse {
  data: Status[];
  total: number;
}

export interface StatusSearchParams {
  page?: number;
  pageSize?: number;
  search?: string;
  active?: boolean;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// API Service
export const statusApi = {
  // CRUD Operations
  getAll: (params?: StatusSearchParams): Promise<StatusListResponse> =>
    api.get('/status', params),

  getById: (id: number): Promise<Status> =>
    api.get(`/status/${id}`),

  create: (status: Omit<Status, 'Id'>): Promise<Status> =>
    api.post('/status', status),

  update: (id: number, status: Partial<Status>): Promise<Status> =>
    api.put(`/status/${id}`, status),

  delete: (id: number): Promise<{ success: boolean; message: string }> =>
    api.delete(`/status/${id}`),

  // Search and Filter
  search: (params: StatusSearchParams): Promise<StatusListResponse> =>
    api.post('/status/search', params),

  // Bulk Operations
  bulkActivate: (ids: number[]): Promise<{ success: boolean; message: string }> =>
    api.put('/status/bulk-activate', { ids }),

  bulkDeactivate: (ids: number[]): Promise<{ success: boolean; message: string }> =>
    api.put('/status/bulk-deactivate', { ids }),

  bulkDelete: (ids: number[]): Promise<{ success: boolean; message: string }> =>
    api.post('/status/bulk-delete', { ids }),

  // Statistics
  getStatistics: (): Promise<{
    total: number;
    active: number;
    inactive: number;
  }> =>
    api.get('/status/statistics'),

  // Export
  exportToCSV: (params?: StatusSearchParams): Promise<Blob> =>
    api.getBlob('/status/export', { params }),

  // Validation
  validation: {
    validateStatus: (status: Partial<Status>): string[] => {
      const errors: string[] = [];

      // StatusName validation
      if (!status.StatusName || status.StatusName.trim().length === 0) {
        errors.push('Status Name is required');
      } else if (status.StatusName.trim().length < 2) {
        errors.push('Status Name must be at least 2 characters long');
      } else if (status.StatusName.trim().length > 100) {
        errors.push('Status Name must be less than 100 characters');
      }

      // Description validation
      if (!status.Description || status.Description.trim().length === 0) {
        errors.push('Description is required');
      } else if (status.Description.trim().length < 2) {
        errors.push('Description must be at least 2 characters long');
      } else if (status.Description.trim().length > 500) {
        errors.push('Description must be less than 500 characters');
      }

      // Active validation
      if (status.Active === undefined || status.Active === null) {
        errors.push('Active status is required');
      }

      return errors;
    },

    validateBulkOperation: (ids: number[]): string[] => {
      const errors: string[] = [];

      if (!ids || ids.length === 0) {
        errors.push('At least one Status must be selected');
      }

      if (ids.length > 100) {
        errors.push('Cannot perform bulk operation on more than 100 items at once');
      }

      return errors;
    }
  }
};

export default statusApi; 